import React, { useState, useEffect, useRef } from 'react';

const ProgressiveImage = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
      loading="lazy"
      onClick={onClick}
      onLoad={() => setIsLoaded(true)}
      onError={() => setIsLoaded(true)} 
    />
  );
};

const ImageModal = ({ image, images, onClose }) => {
  const preloadImages = (images) => {
    images.forEach((image) => {
      const img = new Image();
      img.src = image;
    });
  };
  
  const scrollContainerRef = useRef(null);
  const modalContentRef = useRef(null);
  const scrollTimeout = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Find the initial index based on the selected image
  const initialIndex = images.findIndex(img => img === image);
  const [currentIndex, setCurrentIndex] = useState(initialIndex !== -1 ? initialIndex : 0);
  
  useEffect(() => {
    if (image) {
      preloadImages(images);
    }
    
    // Reset the index when a new image is selected
    const newIndex = images.findIndex(img => img === image);
    if (newIndex !== -1) {
      setCurrentIndex(newIndex);
    }
  }, [image, images]);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') navigateImage(-1);
      if (e.key === 'ArrowRight') navigateImage(1);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]); // Add currentIndex as a dependency

  // Handle wheel and touch events for scrolling through images
  useEffect(() => {
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        // Horizontal scrolling takes priority
        return;
      }
      
      // Prevent too many scroll events from firing rapidly
      if (!isScrolling) {
        setIsScrolling(true);
        
        if (e.deltaY > 0) {
          navigateImage(1); // Scroll down/right = next image
        } else if (e.deltaY < 0) {
          navigateImage(-1); // Scroll up/left = previous image
        }
        
        // Reset the scroll state after a delay
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
          setIsScrolling(false);
        }, 200);
      }
      
      e.preventDefault();
    };

    // Touch event handling for mobile
    let touchStartX = 0;
    let touchEndX = 0;
    
    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };
    
    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };
    
    const handleSwipe = () => {
      const swipeThreshold = 50;
      if (touchEndX < touchStartX - swipeThreshold) {
        navigateImage(1); // Swipe left = next image
      } else if (touchEndX > touchStartX + swipeThreshold) {
        navigateImage(-1); // Swipe right = previous image
      }
    };

    const contentElement = modalContentRef.current;
    if (contentElement) {
      contentElement.addEventListener('wheel', handleWheel, { passive: false });
      contentElement.addEventListener('touchstart', handleTouchStart);
      contentElement.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        contentElement.removeEventListener('wheel', handleWheel);
        contentElement.removeEventListener('touchstart', handleTouchStart);
        contentElement.removeEventListener('touchend', handleTouchEnd);
        clearTimeout(scrollTimeout.current);
      };
    }
  }, [isScrolling, currentIndex]); // Add currentIndex as a dependency

  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const thumbnails = scrollContainer.children;
      if (thumbnails[currentIndex]) {
        thumbnails[currentIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [currentIndex]);

  const navigateImage = (direction) => {
    const newIndex = (currentIndex + direction + images.length) % images.length;
    setCurrentIndex(newIndex);
  };
  
  if (!image) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[1000] flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <button 
          onClick={onClose}
          className="bg-white rounded-full p-2 hover:bg-gray-200 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div ref={modalContentRef} className="max-w-4xl max-h-full relative">
        <img 
          src={images[currentIndex]} 
          alt="Expanded view" 
          className="max-w-full max-h-[85vh] object-contain"
        />
        
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2">
          <button 
            onClick={() => navigateImage(-1)}
            className="bg-white bg-opacity-75 rounded-full p-2 m-2 hover:bg-opacity-100 transition-colors"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
          <button 
            onClick={() => navigateImage(1)}
            className="bg-white bg-opacity-75 rounded-full p-2 m-2 hover:bg-opacity-100 transition-colors"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
      
      <div className="absolute bottom-6 left-1/2 w-full transform -translate-x-1/2">
        <div 
          ref={scrollContainerRef}
          className="flex flex-wrap w-full space-x-2 overflow-x-auto p-2 bg-black bg-opacity-50 rounded-lg justify-center"
        >
          {images.map((img, idx) => (
            <div 
              key={idx}
              onClick={() => setCurrentIndex(idx)} 
              className={`w-12 h-12 flex-shrink-0 cursor-pointer ${currentIndex === idx ? 'ring-2 ring-white' : 'opacity-70'}`}
            >
              <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ImageGallery = ({ images, apartmentName }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollContainerRef = useRef(null);

  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center bg-gray-100 rounded-lg h-48">
        <p className="text-gray-500">No images available for {apartmentName}</p>
      </div>
    );
  }
  
  // Select main image and side images (only 2)
  const mainImage = images[0];
  const sideImages = images.slice(1, 3); // Only take 2 side images
  
  return (
    <div className="flex justify-center items-center w-full">
      <div className="max-w-3xl w-full px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 w-full">
          <div className="md:col-span-8 lg:col-span-8">
            <div className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300">
              <ProgressiveImage
                src={mainImage}
                alt="Main featured"
                className="w-full h-full aspect-square object-cover transform group-hover:scale-105 transition-transform duration-300"
                onClick={() => setSelectedImage(mainImage)}
              />
            </div>
          </div>

          <div className="md:col-span-4 lg:col-span-4 hidden md:block">
            <div className="grid grid-rows-2 gap-3 h-full">
              {sideImages.map((image, index) => (
                <div
                  key={index}
                  className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <ProgressiveImage
                    src={image}
                    alt={`Side image ${index + 1}`}
                    className="w-full h-full aspect-square object-cover transform group-hover:scale-105 transition-transform duration-300"
                    onClick={() => setSelectedImage(image)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {selectedImage && (
        <ImageModal
          image={selectedImage}
          images={images}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default ImageGallery;